import strings from "../core/translate";
import { Scopes } from "./scopes.enum";
import { hasAnyScope } from "./permissions";

// Every section that can be opened from a garage (branch) record.
// Kept in one place so the garages list and the garage sub-pages stay in sync.
export const GARAGE_SECTIONS = [
  {
    key: "services",
    label: () => strings.services,
    icon: "nc-icon nc-settings",
    scopes: [
      Scopes.MERCHANTS_BRANCHES_SERVICES_READ,
      Scopes.MERCHANTS_BRANCHES_SERVICES_WRITE,
    ],
    path: (branchId) => `/admin/merchants-branches/${branchId}/services`,
  },
  {
    key: "ratings",
    label: () => strings.ratings,
    icon: "nc-icon nc-favourite-28",
    scopes: [
      Scopes.MERCHANTS_BRANCHES_RATINGS_READ,
      Scopes.MERCHANTS_BRANCHES_RATINGS_WRITE,
    ],
    path: (branchId) => `/admin/merchants-branches/${branchId}/ratings`,
  },
  {
    key: "appointment-settings",
    label: () => strings.appointment_settings,
    icon: "nc-icon nc-settings-gear-65",
    // grouped under one "Appointments Management" dropdown in the garage
    // login's top nav — see GARAGE_SECTION_GROUPS
    group: "appointments",
    scopes: [
      Scopes.MERCHANTS_BRANCHES_APPOINTMENTS_SETTINGS_READ,
      Scopes.MERCHANTS_BRANCHES_APPOINTMENTS_SETTINGS_WRITE,
    ],
    path: (branchId) =>
      `/admin/merchants-branches/${branchId}/appointments-settings`,
  },
  {
    key: "workers",
    label: () => strings.merchant_workers,
    icon: "nc-icon nc-single-02",
    scopes: [
      Scopes.MERCHANTS_WORKERS_READ,
      Scopes.MERCHANTS_WORKERS_WRITE,
    ],
    path: (branchId) => `/admin/merchants-branches/${branchId}/workers`,
  },
  {
    key: "garage-setup",
    label: () => strings.garage_setup,
    icon: "nc-icon nc-money-coins",
    scopes: [Scopes.GARAGE_SETUP_READ, Scopes.GARAGE_SETUP_WRITE],
    path: (branchId) => `/admin/merchants-branches/${branchId}/garage-setup`,
  },
  {
    key: "job-cards",
    label: () => strings.job_cards,
    icon: "nc-icon nc-paper",
    group: "work-tracking",
    scopes: [Scopes.WORK_ORDERS_READ, Scopes.WORK_ORDERS_WRITE],
    path: (branchId) => `/admin/merchants-branches/${branchId}/job-cards`,
  },
  {
    key: "work-orders",
    label: () => strings.work_orders,
    icon: "nc-icon nc-settings-tool-66",
    group: "work-tracking",
    scopes: [Scopes.WORK_ORDERS_READ, Scopes.WORK_ORDERS_WRITE],
    path: (branchId) => `/admin/merchants-branches/${branchId}/work-orders`,
  },
  {
    key: "appointments",
    label: () => strings.appointments,
    icon: "nc-icon nc-calendar-60",
    group: "appointments",
    scopes: [
      Scopes.MERCHANTS_BRANCHES_APPOINTMENTS_READ,
      Scopes.MERCHANTS_BRANCHES_APPOINTMENTS_WRITE,
    ],
    path: (branchId) => `/admin/merchants-branches/${branchId}/appointments`,
  },
];

// How TopNav groups a garage login's direct sections into dropdowns. Card
// buttons (MerchantBranchList) ignore this and stay flat.
export const GARAGE_SECTION_GROUPS = {
  appointments: {
    label: () => strings.appointments_management,
    icon: "nc-icon nc-calendar-60",
  },
  "work-tracking": {
    label: () => strings.work_tracking,
    icon: "nc-icon nc-settings-tool-66",
  },
};

// Sections nobody browses to: the service catalogue is set up once and then
// left alone, and a work order opens from the job card it belongs to. They are
// kept off the lists that exist to get you into a garage — the top nav and the
// garage cards — and reached from the dashboard or a garage page's own tabs.
const NOT_BROWSED = ["services", "work-orders"];

// Sections the logged in user is allowed to open, resolved for a given garage.
export const getGarageSections = (branchId) =>
  GARAGE_SECTIONS.filter((section) => hasAnyScope(section.scopes)).map(
    (section) => ({
      key: section.key,
      label: section.label(),
      icon: section.icon,
      path: section.path(branchId),
      group: section.group,
    })
  );

// The sections a list of garages offers as a way in — see NOT_BROWSED.
export const getGarageBrowseSections = (branchId) =>
  getGarageSections(branchId).filter(
    (section) => !NOT_BROWSED.includes(section.key)
  );

export const garagesPath = (merchantId) =>
  `/admin/merchant/${merchantId || ":selectedMerchant"}/branches`;
